import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { IconoItem, IconoFamilia } from '@/lib/cd2bIconos'
import { useAuthStore } from '@/store/authStore'

// Campo nativo legible en los dos temas y con alto táctil (40px).
const CAMPO = 'h-10 min-w-0 rounded-md border border-line bg-card px-2.5 text-body-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'

/**
 * Picker de SERVICIOS + complementos que el asesor puede cobrar (implementación, migración,
 * limpieza de datos, import/export, capacitación, soporte, negocio adicional…). Todo se agrega
 * como `cargosAdicionales` (único o recurrente) → entra a la cotización/addenda y a la factura.
 * Controlado por el padre: recibe `cargos` y `setCargos(nuevoArray)`. Los montos son editables
 * (servicios negociables). Disponible en la cotización (empresa nueva) y en la ficha (empresa activa).
 */
export default function CargosServicios({ catalogo, cargos = [], setCargos, moneda = 'USD' }) {
  const servicios = catalogo?.servicios || []
  // El asesor INDEPENDIENTE puede cobrar servicios propios (implementación, capacitación…) en el
  // mismo link: CoreDesk los factura, retiene su tarifa y se los liquida (docs/cd2b-modelo-
  // comercial-asesores.md D2/D3). Para el empleado todo servicio es de CoreDesk.
  const independiente = useAuthStore((st) => st.partner?.vinculo) === 'independiente'
  const [sel, setSel] = useState('')

  const yaAgregado = (code) => code && cargos.some((c) => c.code === code)

  const agregar = (code) => {
    if (!code || yaAgregado(code)) return
    const it = servicios.find((s) => s.code === code)
    if (!it) return
    setCargos([...cargos, { code: it.code, name: it.label, amount: it.precio, frequency: it.frequency || 'once' }])
  }
  const agregarPersonalizado = () =>
    setCargos([...cargos, { code: '', name: '', amount: 0, frequency: 'once' }])
  const editar = (idx, campo, valor) =>
    setCargos(cargos.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)))
  const quitar = (idx) => setCargos(cargos.filter((_, i) => i !== idx))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <label className="text-caption text-neutral-500 dark:text-neutral-400 mb-1 flex items-center gap-1.5">
            <IconoFamilia tipo="servicio" size={13} /> Agregar servicio del integrador
          </label>
          <select
            value={sel}
            onChange={(e) => { agregar(e.target.value); setSel('') }}
            className={`w-full ${CAMPO}`}
          >
            <option value="">Elige un servicio…</option>
            {servicios.map((s) => (
              <option key={s.code} value={s.code} disabled={yaAgregado(s.code)}>
                {s.label} · {fmtMoneda(s.precio, moneda)}{s.frequency === 'monthly' ? '/mes' : ' único'}
              </option>
            ))}
          </select>
        </div>
        <button type="button" onClick={agregarPersonalizado}
          className="inline-flex h-10 w-full sm:w-auto items-center justify-center gap-1 rounded-md border border-line bg-card px-3 text-body-sm text-foreground hover:bg-surface-muted">
          <Plus size={14} /> Cargo personalizado
        </button>
      </div>

      {/* Los add-ons recurrentes (soporte, negocio) se administran con su CHECK en la lista de
          módulos (llevan `origen:'addon'`); aquí NO se listan para no DUPLICARlos y para que su
          frecuencia quede fija en mensual. Esta sección es solo para SERVICIOS del integrador. */}
      {(() => {
        const filas = cargos.map((c, idx) => ({ c, idx })).filter(({ c }) => c.origen !== 'addon')
        if (filas.length === 0) {
          return (
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Ej.: implementación (único), migración/limpieza de datos, capacitación, soporte mensual.
            </p>
          )
        }
        const mensual = filas.filter(({ c }) => (c.frequency || 'once') !== 'once')
          .reduce((s, { c }) => s + (c.frequency === 'annual' ? (Number(c.amount) || 0) / 12 : Number(c.amount) || 0), 0)
        const unico = filas.filter(({ c }) => (c.frequency || 'once') === 'once').reduce((s, { c }) => s + (Number(c.amount) || 0), 0)
        return (
          <div className="space-y-2">
            {filas.map(({ c, idx }) => (
              <div key={idx}
                className="grid grid-cols-6 sm:grid-cols-12 items-center gap-2 rounded-lg border border-line bg-surface-muted p-2.5 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0">
                <span className="order-1 sm:order-none col-span-1 flex justify-center">
                  {/* Del catálogo lleva su icono propio; un cargo personalizado cae al genérico
                      de la familia, que ya distingue "servicio" de módulo o cupo. */}
                  <IconoItem item={{ tipo: 'servicio', icono: (servicios.find((s) => s.code === c.code) || {}).icono }} size={15} />
                </span>
                {/* En el teléfono cada cargo es una tarjeta: concepto + papelera arriba, monto y
                    frecuencia debajo, y «quién lo presta» a todo lo ancho. Desde sm, una fila. */}
                <input className={`order-2 sm:order-none col-span-4 ${independiente ? 'sm:col-span-3' : 'sm:col-span-4'} ${CAMPO}`}
                  aria-label="Concepto" placeholder="Concepto" value={c.name || ''} onChange={(e) => editar(idx, 'name', e.target.value)} />
                <input type="number" min="0" step="0.01" inputMode="decimal" aria-label="Monto"
                  className={`order-4 sm:order-none col-span-3 ${independiente ? 'sm:col-span-2' : 'sm:col-span-3'} ${CAMPO} tabular-nums`}
                  value={c.amount} onChange={(e) => editar(idx, 'amount', Number(e.target.value) || 0)} />
                <select aria-label="Frecuencia" className={`order-5 sm:order-none col-span-3 ${independiente ? 'sm:col-span-2' : 'sm:col-span-3'} ${CAMPO}`}
                  value={c.frequency} onChange={(e) => editar(idx, 'frequency', e.target.value)}>
                  <option value="once">Único</option>
                  <option value="monthly">Mensual</option>
                  <option value="annual">Anual</option>
                </select>
                {independiente && (
                  <select aria-label="Quién lo presta" className={`order-6 sm:order-none col-span-6 sm:col-span-3 ${CAMPO}`}
                    title="Si lo prestas tú, cd-2b lo cobra y te lo liquida menos su tarifa"
                    value={c.de || 'coredesk'}
                    onChange={(e) => editar(idx, 'de', e.target.value)}>
                    <option value="coredesk">Lo presta cd-2b</option>
                    <option value="asesor">Lo presto yo</option>
                  </select>
                )}
                <button type="button" onClick={() => quitar(idx)} aria-label="Quitar cargo"
                  className="order-3 sm:order-none col-span-1 flex h-10 items-center justify-center rounded-md text-danger hover:bg-danger-50 dark:hover:bg-danger/15">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <p className="text-caption text-neutral-600 dark:text-neutral-400 pt-1">
              {mensual > 0 && <>Recurrente: <b className="tabular-nums">{fmtMoneda(mensual, moneda)}/mes</b>. </>}
              {unico > 0 && <>Único (primera factura): <b className="tabular-nums">{fmtMoneda(unico, moneda)}</b>.</>}
            </p>
            {independiente && (
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Los servicios que prestas tú (únicos o mensuales) los cobra y factura cd-2b en el mismo link y en cada
                renovación, y te los liquida menos la tarifa de plataforma. No suman a tu comisión.
              </p>
            )}
          </div>
        )
      })()}
    </div>
  )
}
